import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * shadcn/ui button.
 *
 * Every colour is a semantic token (`bg-primary`, `text-muted-foreground`) rather than
 * a palette value (`bg-blue-600`). That is what makes dark mode a variable swap instead
 * of a second set of classes, and it is why `theme/tokens.ts` can stay the one source.
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        // Transparent, not `bg-background`: the wash has to lighten what is actually
        // behind the button, and on a card `bg-background` is a step DARKER in dark mode.
        outline: 'border border-border-strong bg-transparent hover:bg-hover active:bg-active',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-active',
        ghost: 'hover:bg-hover active:bg-active',
        link: 'text-link underline-offset-4 hover:underline',
      },
      /*
       * Heights come from `--control-height*`, which `theme/tokensCss.ts` generates from
       * `themeShape.controlHeightPx` — the same number `antdTheme.ts` gives AntD. That is
       * what makes an AntD Select and a shadcn Button line up on a filter row.
       */
      size: {
        default: 'h-[var(--control-height)] px-4 py-2',
        sm: 'h-[var(--control-height-sm)] rounded-md px-3 text-xs',
        lg: 'h-[var(--control-height-lg)] rounded-md px-6',
        icon: 'size-[var(--control-height)]',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** Render as the child element instead of a <button>, keeping the styling. */
    asChild?: boolean;
  };

export const Button = ({ className, variant, size, asChild = false, ...props }: ButtonProps) => {
  const Component = asChild ? Slot : 'button';
  return <Component className={cn(buttonVariants({ variant, size }), className)} {...props} />;
};
